import { useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { Button, ErrorNotice } from '../components/ui'

const MIN_LENGTH = 12

/**
 * Shown instead of the app while `mustChangePassword` is set. The server blocks
 * every other endpoint until this is done, so this is not merely cosmetic.
 */
export function ChangePasswordPage({ forced }: { forced: boolean }) {
  const { changePassword, user, signOut } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const mismatch = confirm.length > 0 && newPassword !== confirm
  const tooShort = newPassword.length > 0 && newPassword.length < MIN_LENGTH

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (mismatch || tooShort) return
    setBusy(true)
    try {
      await changePassword(currentPassword, newPassword)
      setDone(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirm('')
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold tracking-tight">
            {forced ? 'Choose your password' : 'Change your password'}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {forced
              ? `Signed in as ${user?.name}. Replace the temporary password you were given before continuing.`
              : 'This will sign you out of your other devices.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-4 p-6">
          <div>
            <label className="label" htmlFor="current">
              {forced ? 'Temporary password' : 'Current password'}
            </label>
            <input
              id="current"
              type="password"
              className="field"
              autoComplete="current-password"
              required
              autoFocus
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="new">
              New password
            </label>
            <input
              id="new"
              type="password"
              className="field"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              At least {MIN_LENGTH} characters. A short phrase you can remember beats a short
              scramble you cannot.
            </p>
          </div>

          <div>
            <label className="label" htmlFor="confirm">
              Confirm new password
            </label>
            <input
              id="confirm"
              type="password"
              className="field"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {mismatch && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">Passwords do not match.</p>
            )}
          </div>

          {error != null && <ErrorNotice error={error} />}
          {done && !forced && (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
              Password updated.
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={busy}
            disabled={mismatch || tooShort || !newPassword || !currentPassword}
          >
            Update password
          </Button>

          {forced && (
            <button
              type="button"
              onClick={() => void signOut()}
              className="w-full text-center text-xs text-slate-500 hover:underline dark:text-slate-400"
            >
              Sign out instead
            </button>
          )}
        </form>
      </div>
    </div>
  )
}
